import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { business } from "@/lib/business-data";

const SCRIPT_SRC = "https://reputationhub.site/reputation/assets/review-widget.js";
const WIDGET_SRC =
  "https://reputationhub.site/reputation/widgets/review_widget/r8ma6AsmSIhQa35Wz0XI?widgetId=6a7a08daf50284472cb2d569";

/**
 * Reviews embed, rendered verbatim in its original page position.
 *
 * The widget sizes itself via its own script — there is deliberately no
 * height, max-height, overflow, or slider wrapper anywhere in this component.
 * It expands to whatever height its content needs.
 */
const ReviewWidget = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);

  /**
   * The reviews embed sits below the fold. Neither its script nor its iframe is
   * requested until the section is close to the viewport, so the third-party
   * code stays out of the initial load and off the main thread during startup.
   */
  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "400px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!inView) return;
    if (document.querySelector(`script[src="${SCRIPT_SRC}"]`)) return;
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src = SCRIPT_SRC;
    script.async = true;
    document.body.appendChild(script);
  }, [inView]);

  return (
    <section id="reviews" ref={sectionRef} className="section-padding section-soft-alt">
      <div className="container-max">
        <div className="text-center mb-12 max-w-2xl mx-auto">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="eyebrow-pill mb-5"
          >
            <Star className="h-3 w-3" /> Customer Reviews
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-foreground tracking-tight font-bold leading-[1.05]"
            style={{ fontSize: "clamp(2.25rem, 4.5vw, 4rem)" }}
          >
            What Our <span className="text-gradient-primary">Neighbors</span> Say
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-muted-foreground text-lg leading-relaxed mt-5"
          >
            Real reviews from homeowners across Hayward and the East Bay.
          </motion.p>
        </div>

        <div className="widget-embed mx-auto max-w-5xl">
          {inView && (
            <iframe
              className="lc_reviews_widget"
              src={WIDGET_SRC}
              loading="lazy"
              frameBorder="0"
              scrolling="no"
              title={`Customer reviews for ${business.name}`}
              style={{ minWidth: "100%", width: "100%" }}
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default ReviewWidget;
